import { CompetitorsMediaInvestmentSearch } from '@/dashboards/CompetitorsMediaInvestment';
import { groupCompetitorsMediaInvestmentData } from '@/dashboards/CompetitorsMediaInvestment/helpers/groupCompetitorsMediaInvestmentData';
import { useCompetitorMediaEffectQuery } from '@/dashboards/CompetitorsMediaInvestment/hooks/useCompetitorMediaEffectQuery';
import { useCompetitorsMediaInvestmentQuery } from '@/dashboards/CompetitorsMediaInvestment/hooks/useCompetitorsMediaInvestmentQuery';
import { useCompetitorsMediaInvestmentUrl } from '@/dashboards/CompetitorsMediaInvestment/hooks/useCompetitorsMediaInvestmentUrl';
import { useKpiData } from '@/dashboards/CompetitorsMediaInvestment/hooks/useKpiData';
import {
    CompetitorImpact,
    FilterTypes,
    FormattedCompetitorsMediaInvestmentData,
    KpiData,
} from '@/dashboards/CompetitorsMediaInvestment/types';
import { makeDateFormatter } from '@/utils/dateUtils';
import {
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from 'react';
import { createContext } from 'use-context-selector';

export const CompetitorsMediaInvestmentContext = createContext<{
    competitorsMediaInvestmentData: FormattedCompetitorsMediaInvestmentData;
    competitorsMediaEffectData: FormattedCompetitorsMediaInvestmentData;
    kpiData: KpiData;
    missingVariables: string[];
    selectedCategoryFilter: FilterTypes[];
    getVisibleInvestmentData: () => {
        [key: string]: {
            data: { [key: string]: number };
            color: string;
        };
    };
    getVisibleContributionData: () => {
        [key: string]: {
            data: { [key: string]: number };
            color: string;
        };
    };
    getCompetitorImpact: (variable: string) => CompetitorImpact;
    setSelectedCategory: (name: string, checked: boolean) => void;
    selectedCompetitorFilter: FilterTypes[];
    setSelectedCompetitor: (name: string, checked: boolean) => void;
    period: CompetitorsMediaInvestmentSearch['period'];
    isLoading: boolean;
    isFetching: boolean;
}>({
    competitorsMediaInvestmentData:
        {} as FormattedCompetitorsMediaInvestmentData,
    competitorsMediaEffectData: {} as FormattedCompetitorsMediaInvestmentData,
    kpiData: [],
    missingVariables: [],
    getVisibleInvestmentData: () => ({}),
    getVisibleContributionData: () => ({}),
    getCompetitorImpact: () => 'neutral',
    setSelectedCategory: () => {},
    selectedCategoryFilter: [],
    selectedCompetitorFilter: [],
    setSelectedCompetitor: () => {},
    period: 'weekly',
    isLoading: false,
    isFetching: false,
});

export const CompetitorsMediaInvestmentProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const [selectedCompetitorFilter, setSelectedCompetitorFilter] = useState<
        FilterTypes[]
    >([]);
    const { search, params } = useCompetitorsMediaInvestmentUrl();

    const {
        queryResult,
        selectedCategoryFilter,
        setSelectedCategoryFilter,
        selectedCategories,
    } = useCompetitorsMediaInvestmentQuery();
    const { queryResult: kpiQueryResult } = useKpiData();
    const { queryResult: effectQueryResult } = useCompetitorMediaEffectQuery();

    const {
        data: investmentData,
        isFetching: isInvestmentFetching,
        isLoading: isInvestmentLoading,
        dataUpdatedAt: investmentDataUpdatedAt,
    } = queryResult;
    const {
        data: kpiData,
        isLoading: isKpiLoading,
        isFetching: isKpiFetching,
    } = kpiQueryResult;
    const {
        data: effectQueryData,
        isLoading: isEffectLoading,
        isFetching: isEffectFetching,
        dataUpdatedAt: effectDataUpdatedAt,
    } = effectQueryResult;

    const isLoading = isInvestmentLoading || isKpiLoading || isEffectLoading;
    const isFetching =
        isInvestmentFetching || isKpiFetching || isEffectFetching;

    const competitorsMediaInvestmentData = useMemo(() => {
        if (
            !investmentData?.rows ||
            investmentData.rows.length === 0 ||
            !investmentData?.fieldKeys
        ) {
            return {
                investmentDataByVariable: new Map(),
                investmentDataByCategory: new Map(),
                totalInvestment: 0,
                totalInvestmentByCategory: new Map(),
                totalInvestmentByVariable: new Map(),
                variableToCategory: new Map(),
                variableData: [],
                categoryData: [],
                categoryFilters: [],
                competitorFilters: [],
                dates: [],
            };
        }
        return groupCompetitorsMediaInvestmentData(
            investmentData.rows,
            investmentData.fieldKeys,
            {
                formatDate: makeDateFormatter(search.period),
                startDate: search.startDate,
                endDate: search.endDate,
                cacheKey: `${params.projectId}-${search.period}-${investmentDataUpdatedAt} - ${selectedCategories.join(',')}`,
            },
        );
    }, [
        investmentData?.fieldKeys,
        investmentData?.rows,
        investmentDataUpdatedAt,
        selectedCategories,
        params.projectId,
        search.endDate,
        search.period,
        search.startDate,
    ]);

    const competitorsMediaEffectData = useMemo(() => {
        if (!effectQueryData?.rows || !effectQueryData?.fieldKeys) {
            return {
                investmentDataByVariable: new Map(),
                investmentDataByCategory: new Map(),
                totalInvestment: 0,
                totalInvestmentByCategory: new Map(),
                totalInvestmentByVariable: new Map(),
                variableToCategory: new Map(),
                variableData: [],
                categoryData: [],
                categoryFilters: [],
                competitorFilters: [],
                dates: [],
            };
        }
        return groupCompetitorsMediaInvestmentData(
            effectQueryData.rows,
            effectQueryData.fieldKeys,
            {
                formatDate: (dateString) => dateString,
                startDate: search.startDate,
                endDate: search.endDate,
                cacheKey: `${params.projectId}-${search.period}-${effectDataUpdatedAt}-effect`,
            },
        );
    }, [
        effectQueryData?.fieldKeys,
        effectQueryData?.rows,
        effectDataUpdatedAt,
        params.projectId,
        search.endDate,
        search.period,
        search.startDate,
    ]);

    const { investmentDataByVariable } = competitorsMediaInvestmentData;

    // The spend breakdown (input) and the modeled effect (output) can reference
    // disjoint variables: when a "total" competitor variable drives the effect,
    // it is excluded from the spend breakdown but must still appear as its own
    // series + filter chip in the effect graph. Build the competitor filter set
    // as the union of both sources, deduped by name (spend entries first).
    const competitorFilters = useMemo(() => {
        const merged = new Map<string, FilterTypes>();

        for (const filter of [
            ...competitorsMediaInvestmentData.competitorFilters,
            ...competitorsMediaEffectData.competitorFilters,
        ]) {
            if (!merged.has(filter.name)) {
                merged.set(filter.name, filter);
            }
        }

        return Array.from(merged.values());
    }, [
        competitorsMediaInvestmentData.competitorFilters,
        competitorsMediaEffectData.competitorFilters,
    ]);

    const effectiveCompetitorFilter =
        selectedCompetitorFilter.length > 0
            ? selectedCompetitorFilter
            : competitorFilters;

    const setSelectedCategory = (name: string, checked: boolean) => {
        const updatedCategoryFilters = selectedCategoryFilter.map(
            (categoryObj) => {
                if (name === 'all') {
                    return { ...categoryObj, selected: checked };
                }
                if (categoryObj.name === name) {
                    return { ...categoryObj, selected: checked };
                }
                return categoryObj;
            },
        );

        setSelectedCategoryFilter(updatedCategoryFilters);
    };

    const setSelectedCompetitor = (name: string, checked: boolean) => {
        const filters = effectiveCompetitorFilter.map((competitorObj) => {
            if (name === 'all') {
                return { ...competitorObj, selected: checked };
            }
            if (competitorObj.name === name) {
                return { ...competitorObj, selected: checked };
            }
            return competitorObj;
        });

        setSelectedCompetitorFilter(filters);
    };

    const getVisibleInvestmentData = useCallback(() => {
        return Object.fromEntries(
            Array.from(investmentDataByVariable).filter(([name, _]) => {
                return effectiveCompetitorFilter.some(
                    (filterObj) =>
                        filterObj.name === name && filterObj.selected,
                );
            }),
        );
    }, [investmentDataByVariable, effectiveCompetitorFilter]);

    const getVisibleContributionData = useCallback(() => {
        return Object.fromEntries(
            Array.from(
                competitorsMediaEffectData.investmentDataByVariable,
            ).filter(([name, _]) => {
                return effectiveCompetitorFilter.some(
                    (filterObj) =>
                        filterObj.name === name && filterObj.selected,
                );
            }),
        );
    }, [
        competitorsMediaEffectData.investmentDataByVariable,
        effectiveCompetitorFilter,
    ]);

    const getCompetitorImpact = useCallback(
        (variable: string) => {
            const totalImpact =
                competitorsMediaEffectData.totalInvestmentByVariable.get(
                    variable,
                );

            if (totalImpact > 0) {
                return 'positive';
            }
            if (totalImpact < 0) {
                return 'negative';
            }
            return 'neutral';
        },
        [competitorsMediaEffectData.totalInvestmentByVariable],
    );

    return (
        <CompetitorsMediaInvestmentContext.Provider
            value={{
                getVisibleInvestmentData,
                getVisibleContributionData,
                getCompetitorImpact,
                competitorsMediaInvestmentData,
                kpiData: kpiData || [],
                missingVariables: investmentData?.missingVariables ?? [],
                competitorsMediaEffectData,
                setSelectedCategory,
                selectedCategoryFilter,
                selectedCompetitorFilter: effectiveCompetitorFilter,
                setSelectedCompetitor,
                period: search.period,
                isLoading,
                isFetching,
            }}
        >
            {children}
        </CompetitorsMediaInvestmentContext.Provider>
    );
};
